/**
 * A document as a peek: what it is, the run that stored it, its extracted fields and the payload as received. A
 * field clicked is lit in the payload. Fields are named as people say them ("Sales order number"); a "raw" pill
 * shows their paths instead. The fields sit beside the payload when the panel is wide enough (a container query)
 * and above it otherwise. The payload is a quiet code surface in the tokens, the same in both themes.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Api, ApiError, type Message } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useAsync } from '../lib/hooks.ts';
import { bytes, dateTime, prettyPayload } from '../lib/format.ts';
import { fieldLine } from '../lib/payload.ts';
import { messageSystem, stripSystem } from '../lib/systems.ts';
import { DownloadIcon, FilterIcon } from '../components/icons.tsx';
import { Chip, Glyph, Kicker, NounIcon, Pill, SystemBadge, cx, noun } from '../design/primitives.tsx';
import type { MessageRef } from './usePeek.ts';
import { StatusMark } from './RunPeek.tsx';
import { usePeekParam } from './usePeekView.ts';
import { fieldLabels, isIdentifier } from './fields.ts';

const MAX_PAYLOAD = 2 * 1024 * 1024;

// ---------------------------------------------------------------- tokens of a payload line, in the tokens of the design

type Kind = 'punct' | 'tag' | 'attr' | 'string' | 'number' | 'literal' | 'text' | 'meta';
interface Token { s: string; k: Kind }
const TONE: Record<Kind, string> = {
  punct: 'text-n6', tag: 'text-accent', attr: 'text-n8', string: 'text-n10', number: 'text-run', literal: 'text-run', text: 'text-n10', meta: 'text-n7',
};
// The same grammar as lib/payload.ts, which colours for the old dark island; this one names what a token is
const XML_RE = /(<\/?)([\w:.-]+)|(\s[\w:.-]+=)("[^"]*"|'[^']*')|(\/?>)|(<\?[^?]*\?>|<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>)|([^<>]+)/g;
const JSON_RE = /("(?:[^"\\]|\\.)*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|(true|false|null)|([{}[\],])|(\s+)|(.)/g;

function tokenize(line: string, type: string): Token[] {
  const out: Token[] = [];
  let m: RegExpExecArray | null;
  if (type === 'xml') {
    XML_RE.lastIndex = 0;
    while ((m = XML_RE.exec(line)) !== null) {
      if (m[6]) out.push({ s: m[6], k: 'meta' });
      else if (m[1]) out.push({ s: m[1], k: 'punct' }, { s: m[2], k: 'tag' });
      else if (m[3]) out.push({ s: m[3], k: 'attr' }, { s: m[4], k: 'string' });
      else if (m[5]) out.push({ s: m[5], k: 'punct' });
      else if (m[7]) out.push({ s: m[7], k: 'text' });
    }
    return out;
  }
  if (type === 'json') {
    JSON_RE.lastIndex = 0;
    while ((m = JSON_RE.exec(line)) !== null) {
      if (m[1] && m[2]) out.push({ s: m[1], k: 'tag' }, { s: m[2], k: 'punct' });
      else if (m[1]) out.push({ s: m[1], k: 'string' });
      else if (m[3]) out.push({ s: m[3], k: 'number' });
      else if (m[4]) out.push({ s: m[4], k: 'literal' });
      else if (m[5]) out.push({ s: m[5], k: 'punct' });
      else out.push({ s: m[0], k: 'text' });
    }
    return out;
  }
  return [{ s: line, k: 'text' }];
}

const systemOfMessage = (m: Message) => messageSystem(m.meta, m.messageClass);

export function DocumentPeek({ ref, onRun }: {
  /** The document by where it is; not a React ref. */
  ref: MessageRef;
  onRun: (executionId: string) => void;
}) {
  const { tenant } = useAuth();
  const tid = tenant?.id ?? '';
  const { vaultId, uuid, day } = ref;
  const res = useAsync<Message>(() => Api.message(tid, vaultId, uuid, day), [tid, vaultId, uuid, day]);
  const payload = useAsync<{ text: string; size: number; truncated: boolean }>(async () => {
    const r = await fetch(Api.payloadUrl(tid, vaultId, uuid, day), { credentials: 'same-origin' });
    if (!r.ok) throw new ApiError(r.status, (await r.json().catch(() => ({}))).error ?? r.statusText);
    const buf = await r.arrayBuffer();
    return { text: new TextDecoder('utf-8').decode(buf.slice(0, MAX_PAYLOAD)), size: buf.byteLength, truncated: buf.byteLength > MAX_PAYLOAD };
  }, [tid, vaultId, uuid, day]);
  const m = res.data;

  // The peek's view state is in the URL (replace), so Back from a run opened over this document finds it as it was
  const [selParam, setSelParam] = usePeekParam('psel');
  const [filterParam, setFilterParam] = usePeekParam('pf');
  const [rawParam, setRawParam] = usePeekParam('praw');
  const [firstLit, setFirstLit] = useState(-1);
  const selected = selParam !== null && /^\d+$/.test(selParam) ? Number(selParam) : firstLit;
  const setSelected = (i: number) => setSelParam(String(i));
  const [filter, setFilterState] = useState(filterParam ?? '');
  const setFilter = (v: string) => {
    setFilterState(v);
    setFilterParam(v || null);
  };
  const raw = rawParam === '1';
  const labels = useMemo(() => fieldLabels((m?.fields ?? []).map(([p]) => p)), [m]);
  const pre = useRef<HTMLPreElement>(null);
  const lineRefs = useRef<(HTMLDivElement | null)[]>([]);

  const type = m?.type ?? 'txt';
  const lines = useMemo(() => (payload.data ? prettyPayload(payload.data.text, type).split('\n') : []), [payload.data, type]);
  const tokens = useMemo(() => lines.map((l) => {
    const indent = /^\s*/.exec(l)![0];
    return { indent, tokens: tokenize(l.slice(indent.length), type) };
  }), [lines, type]);

  // The line of the selected field: the n-th occurrence of the same path
  const selectedLine = useMemo(() => {
    if (!m || selected < 0 || selected >= m.fields.length) return -1;
    const [path, value] = m.fields[selected];
    const occurrence = m.fields.slice(0, selected).filter(([p]) => p === path).length;
    return fieldLine(lines, type, path, value, occurrence);
  }, [m, selected, lines, type]);

  // Scroll the payload (only it, not the page) to the lit line
  useEffect(() => {
    const el = lineRefs.current[selectedLine];
    const box = pre.current;
    if (selectedLine < 0 || !el || !box) return;
    box.scrollTo({ top: Math.max(0, el.offsetTop - box.clientHeight / 2), behavior: 'smooth' });
  }, [selectedLine]);

  // The first id-like field starts lit
  useEffect(() => {
    if (!m || selected >= 0) return;
    const i = m.fields.findIndex(([p]) => /(Id|Number|Code)$/i.test(p));
    if (i >= 0) setFirstLit(i);
  }, [m]); // eslint-disable-line react-hooks/exhaustive-deps

  if (res.error) return <p className="m-0 pt-2 text-small text-fail">{res.error}</p>;
  if (!m) return <DocumentSkeleton />;

  const failed = m.meta.status === 'FAILED';
  const q = filter.trim().toLowerCase();
  const fields = m.fields.map(([path, value], i) => ({ path, value, label: labels[i] ?? path, i }))
    .filter((f) => !q || f.path.toLowerCase().includes(q) || f.label.toLowerCase().includes(q) || f.value.toLowerCase().includes(q));

  return (
    <article className="flex flex-col gap-6 pt-2 @container">
      <header className="flex flex-col gap-2">
        <h2 className="m-0 text-title font-semibold tracking-tight [overflow-wrap:anywhere]">{noun(m.messageClass)}</h2>
        <div className="flex items-center gap-2.5 flex-wrap text-small text-n8">
          <SystemBadge code={systemOfMessage(m)} size={20} />
          <StatusMark status={m.meta.status} />
          <span className="font-mono text-n7 whitespace-nowrap">{dateTime(m.timestamp)}</span>
        </div>
        {m.meta.executionId && (
          <button type="button" onClick={() => onRun(m.meta.executionId)}
            className="self-start inline-flex items-center gap-2 h-7 pl-1.5 pr-2.5 -ml-1.5 rounded-full bg-transparent border-0 cursor-pointer text-small text-n8 transition-colors duration-[var(--t-micro)] hover:bg-n3 hover:text-n10">
            <NounIcon kind="run" size={14} />
            <span>stored by <span className="text-n10 font-medium">{stripSystem(m.meta.process) || 'a run'}</span></span>
          </button>
        )}
        {m.meta.info && <p className={cx('m-0 text-small', failed ? 'text-fail' : 'text-n8')}>{m.meta.info}</p>}
        {m.parseError && <p className="m-0 text-micro text-warn">{m.parseError}</p>}
      </header>

      <div className="grid grid-cols-1 gap-5 @lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] min-w-0">
        <section className="flex flex-col gap-2 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <Kicker>Fields · {m.fields.length}</Kicker>
            <span className="flex items-center gap-1.5">
              {m.fields.length > 0 && (
                <span className="inline-flex bg-n3 rounded-full p-0.5">
                  <Pill active={raw} onClick={() => setRawParam(raw ? null : '1')}><span className="font-mono">raw</span></Pill>
                </span>
              )}
              <label className="inline-flex items-center gap-1.5 h-7 pl-2.5 pr-2 rounded-full bg-n3 text-n8">
                <FilterIcon size={12} />
                <span className="sr-only">Filter fields</span>
                <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter"
                  className="bg-transparent border-0 outline-none w-20 text-small text-n10 placeholder:text-n7 p-0" />
              </label>
            </span>
          </div>
          <div className="flex flex-col -mx-2 overflow-y-auto max-h-[38vh] @lg:max-h-[calc(100vh-var(--shell-h,56px)-300px)]">
            {fields.length === 0 && <p className="m-0 px-2 py-3 text-small text-n7">{m.fields.length === 0 ? `No fields were extracted (${m.type} payload).` : 'No field matches.'}</p>}
            {fields.map((f) => (
              <button key={f.i} type="button" onClick={() => setSelected(f.i)} aria-pressed={f.i === selected}
                className={cx('text-left flex flex-col gap-0.5 px-2 py-1.5 rounded-chip border-0 cursor-pointer min-w-0 transition-colors duration-[var(--t-micro)]',
                  f.i === selected ? 'bg-accent-soft' : 'bg-transparent hover:bg-n3')}>
                {raw
                  ? <span className="font-mono text-micro text-n7 [overflow-wrap:anywhere]">{f.path}</span>
                  : <span className="text-micro text-n7 [overflow-wrap:anywhere]">{f.label}</span>}
                <span className={cx('text-small [overflow-wrap:anywhere]', f.i === selected ? 'text-n10 font-medium' : 'text-n9', isIdentifier(f.value) && 'font-mono')}>
                  {f.value === '' ? <i className="text-n7">(empty)</i> : f.value}
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="flex flex-col gap-2 min-w-0" aria-label="Payload">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Kicker>Payload</Kicker>
            <span className="flex items-center gap-2">
              <Chip icon={<Glyph format={m.type} size={16} failed={failed} />}>
                {m.type.toUpperCase()}{payload.data && <> · {bytes(payload.data.size)}</>}
              </Chip>
              <a href={Api.payloadUrl(tid, vaultId, uuid, day, true)} download
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-chip bg-n3 text-small font-medium text-n9 no-underline transition-colors duration-[var(--t-micro)] hover:bg-n4 hover:text-n10">
                <DownloadIcon size={14} />Download
              </a>
            </span>
          </div>
          {payload.data?.truncated && <p className="m-0 text-micro text-warn">first 2 MB shown</p>}
          {payload.error && <p className="m-0 text-small text-fail">{payload.error}</p>}
          <pre ref={pre} className="relative m-0 rounded-card bg-n1 py-3 font-mono text-small leading-[1.65] text-n9 overflow-auto max-h-[56vh] @lg:max-h-[calc(100vh-var(--shell-h,56px)-300px)]">
            {payload.loading && !payload.data && (
              <div className="flex flex-col gap-2 px-4" aria-busy="true">
                {[72, 48, 64, 40, 56].map((w, i) => <span key={i} className="h-3 rounded-chip mv-shimmer" style={{ width: `${w}%` }} />)}
              </div>
            )}
            {tokens.map((l, i) => (
              <div key={i} ref={(el) => { lineRefs.current[i] = el; }}
                className={cx('px-3 whitespace-pre border-l-2', i === selectedLine ? 'bg-accent-soft border-accent' : 'border-transparent')}>
                <span className="inline-block w-8 pr-3 text-right text-n6 select-none">{i + 1}</span>
                <span>{l.indent}</span>
                {l.tokens.map((t, k) => <span key={k} className={TONE[t.k]}>{t.s}</span>)}
              </div>
            ))}
            {payload.data && !payload.data.text && <div className="px-4 text-n7">(empty payload)</div>}
          </pre>
        </section>
      </div>
    </article>
  );
}

/** The document's layout before its data: a title, a facts line, fields and a payload block. */
function DocumentSkeleton() {
  return (
    <div className="flex flex-col gap-6 pt-2" aria-busy="true">
      <div className="flex flex-col gap-2">
        <span className="h-7 w-48 rounded-chip mv-shimmer" />
        <span className="h-4 w-64 rounded-chip mv-shimmer" />
        <span className="h-4 w-56 rounded-chip mv-shimmer" />
      </div>
      <div className="flex flex-col gap-2">
        <span className="h-3 w-20 rounded-chip mv-shimmer" />
        {[0, 1, 2, 3].map((i) => <span key={i} className="h-9 rounded-chip mv-shimmer" style={{ width: `${90 - i * 10}%` }} />)}
      </div>
      <div className="flex flex-col gap-2">
        <span className="h-3 w-20 rounded-chip mv-shimmer" />
        <span className="h-56 rounded-card mv-shimmer" />
      </div>
    </div>
  );
}
